<script setup lang="ts">
import { ref, watch } from 'vue'
import type { ChartConfiguration } from '@/stores/type/chartConfiguration.type'
import { DEFAULT_CHART_CONFIGURATION } from '@/stores/chartConfiguration'
import {
  CHART_AXIS_GROUPING_OPTIONS,
  CHART_DISPLAY_MODE_OPTIONS,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_SORT_ORDER,
  DEFAULT_SELECTED_PHASES
} from '@/constants/chartConfiguration'

export type PivotFormData = {
  name: string
  x_axis_title: string
  y_axis_title: string
  x_axis_grouping: string
  selected_phases: string[]
  display_mode: string
  sort_order: string
  filter_material_categories: string[]
}

const props = defineProps<{
  chartConfiguration: ChartConfiguration | null
  availableCategories: string[]
}>()
const dialogOpen = defineModel<boolean>({ required: true })

const emit = defineEmits<{
  save: [form: PivotFormData]
}>()

const form = ref<PivotFormData>({
  name: '',
  x_axis_title: DEFAULT_CHART_CONFIGURATION.x_axis_title,
  y_axis_title: DEFAULT_CHART_CONFIGURATION.y_axis_title,
  x_axis_grouping: DEFAULT_CHART_CONFIGURATION.x_axis_grouping,
  selected_phases: [...DEFAULT_SELECTED_PHASES],
  display_mode: DEFAULT_DISPLAY_MODE,
  sort_order: DEFAULT_SORT_ORDER,
  filter_material_categories: []
})

watch(dialogOpen, (open) => {
  if (!open) return
  form.value = {
    name: props.chartConfiguration?.name ?? '',
    x_axis_title:
      props.chartConfiguration?.x_axis_title ?? DEFAULT_CHART_CONFIGURATION.x_axis_title,
    y_axis_title:
      props.chartConfiguration?.y_axis_title ?? DEFAULT_CHART_CONFIGURATION.y_axis_title,
    x_axis_grouping:
      props.chartConfiguration?.x_axis_grouping ?? DEFAULT_CHART_CONFIGURATION.x_axis_grouping,
    selected_phases: props.chartConfiguration?.selected_phases
      ? [...props.chartConfiguration.selected_phases]
      : [...DEFAULT_SELECTED_PHASES],
    display_mode: props.chartConfiguration?.display_mode ?? DEFAULT_DISPLAY_MODE,
    sort_order: props.chartConfiguration?.sort_order ?? DEFAULT_SORT_ORDER,
    filter_material_categories: props.chartConfiguration?.filter_material_categories ?? []
  }
})

const isEditMode = ref(false)
watch(
  () => props.chartConfiguration,
  (config) => {
    isEditMode.value = config !== null && config.id !== 0
  },
  { immediate: true }
)

const handleSave = () => {
  emit('save', { ...form.value })
  dialogOpen.value = false
}
</script>

<template>
  <v-dialog v-model="dialogOpen" max-width="560">
    <v-card>
      <v-card-title class="text-h6 pt-5 px-6">
        {{ isEditMode ? 'Edit chart' : 'New chart' }}
      </v-card-title>

      <v-card-text class="px-6 pb-2">
        <!-- Chart name -->
        <div class="text-subtitle-2 mb-1">Chart name</div>
        <v-text-field
          v-model="form.name"
          placeholder="e.g. Scenario A"
          density="comfortable"
          variant="outlined"
          maxlength="100"
          hide-details="auto"
          class="mb-4"
        />

        <!-- Axis labels -->
        <div class="text-subtitle-2 mb-1">Axis labels</div>
        <v-row dense class="mb-4">
          <v-col cols="6">
            <v-text-field
              v-model="form.x_axis_title"
              label="X-axis label"
              density="comfortable"
              variant="outlined"
              maxlength="100"
              hide-details
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.y_axis_title"
              label="Y-axis label"
              density="comfortable"
              variant="outlined"
              maxlength="100"
              hide-details
            />
          </v-col>
        </v-row>

        <!-- Pivot settings -->
        <div class="text-subtitle-2 mb-1">Pivot settings</div>
        <v-row dense class="mb-4">
          <v-col cols="6">
            <v-select
              v-model="form.x_axis_grouping"
              :items="CHART_AXIS_GROUPING_OPTIONS"
              item-title="title"
              item-value="value"
              label="Group X-axis by"
              density="comfortable"
              variant="outlined"
              hide-details
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="form.display_mode"
              :items="CHART_DISPLAY_MODE_OPTIONS"
              item-title="title"
              item-value="value"
              label="Display mode"
              density="comfortable"
              variant="outlined"
              hide-details
            />
          </v-col>
        </v-row>

        <!-- Material filter -->
        <div class="text-subtitle-2 mb-1">
          Filter materials
          <span class="text-caption text-grey ml-1">(leave empty to show all)</span>
        </div>
        <v-combobox
          v-model="form.filter_material_categories"
          :items="props.availableCategories"
          multiple
          chips
          closable-chips
          density="comfortable"
          variant="outlined"
          placeholder="Select materials…"
          hide-details="auto"
        />
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-spacer />
        <v-btn variant="text" @click="dialogOpen = false">Cancel</v-btn>
        <v-btn color="primary" :disabled="!form.name.trim()" @click="handleSave">
          {{ isEditMode ? 'Save' : 'Create chart' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
